import { FormControl, SxProps } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { ReactNode, useState } from 'react';
import { ClearIcon } from './ClearIcon';
import FieldLabel from '../../FieldLabel';
import { DateTime } from 'luxon';
import { humanizeDate, humanizeDateVariants } from '../../../helpers/humanizeDate';
import { renderTimeViewClock } from '@mui/x-date-pickers';
import { Clock } from '@sweep-io/sweep-design/dist/icons/Clock';

export interface TimeSelectorProps {
  initialValue?: string; //in SQL format
  onChange: (value: string) => void;
  shouldBeDisabled?: boolean;
  textFieldSx?: SxProps;
  placeholder?: string;
  label?: string;
  infoTooltipTitle?: NonNullable<ReactNode>;
  showTooltip?: boolean;
  id?: string;
  removeBorders?: boolean;
  fullWidth?: boolean;
  viewRenderers?: boolean;
  timePickerSx?: SxProps;
}

const TimeSelector = ({
  initialValue,
  onChange,
  shouldBeDisabled,
  textFieldSx,
  placeholder,
  label,
  showTooltip,
  infoTooltipTitle,
  id,
  removeBorders,
  fullWidth,
  viewRenderers,
  timePickerSx,
}: TimeSelectorProps) => {
  const [newValue, setValue] = useState(initialValue ? DateTime.fromISO(initialValue) : undefined);

  const handleSetValue = (value?: DateTime | null) => {
    const parsedVal = value?.toSQLTime({ includeOffset: false }) ?? '';
    setValue(value ?? undefined);
    onChange(parsedVal);
  };

  return (
    <FormControl fullWidth={fullWidth}>
      {label && (
        <FieldLabel
          label={label}
          id={id}
          infoTooltipTitle={infoTooltipTitle}
          showTooltip={showTooltip}
        />
      )}

      <TimePicker
        slots={{
          openPickerIcon: () => <Clock size="medium" />,
        }}
        disabled={shouldBeDisabled}
        value={newValue}
        defaultValue={newValue}
        onChange={handleSetValue}
        sx={{
          '& .MuiIconButton-edgeEnd': {
            padding: '4px',
            right: '8px',
          },
          fieldset: {
            border: removeBorders ? 'none' : 'auto',
          },
        }}
        viewRenderers={
          viewRenderers
            ? {
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock,
              }
            : undefined
        }
        slotProps={{
          textField: (params) => ({
            disabled: shouldBeDisabled,
            inputProps: {
              ...params.inputProps,
              placeholder: placeholder ?? 'Choose time',
              disabled: shouldBeDisabled,
              value: humanizeDate({
                dateOrTimestamp: newValue?.toSQL({ includeOffset: false }) ?? '',
                variant: humanizeDateVariants.TIME_WITHOUT_DATE_2_DIGITS,
              }),
            },
            sx: { ...params.sx, ...textFieldSx },
            error: false,
            InputProps: {
              endAdornment: (
                <>
                  {newValue && !shouldBeDisabled && (
                    <ClearIcon clearValue={() => handleSetValue(undefined)} />
                  )}
                  {params.InputProps?.endAdornment}
                </>
              ),
            },
          }),
          popper: {
            sx: {
              ...timePickerSx,
            },
          },
        }}
      />
    </FormControl>
  );
};

export { TimeSelector };
